/**
 * Fluidité B4 (P5#19, P5#20) — « Clôturer le chantier », distinct de la suppression.
 *
 * Associé et administrateur : vérifications (PV, réserves, GPA, DOE, retenues), puis
 * clôture. L'archive complète (PV, réserves avec photos et preuves, signatures, CR,
 * documents, plans annotés, situations) est préparée et conservée ; le client et les
 * entreprises passent en lecture seule jusqu'à la fin de la GPA, puis leur accès est fermé.
 * API : backend/routes/fin_chantier_routes.py (/cloture) et l'archive existante (/export-archive).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, CheckCircle, Warning, DownloadSimple, LockKey } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dateFr = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).length <= 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('fr-FR');
};

export default function ClotureChantier({ chantierId, isMoe, refreshKey }) {
  const [etat, setEtat] = useState(null);
  const [ouvrir, setOuvrir] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/cloture`, { withCredentials: true });
      setEtat(data);
    } catch {
      setEtat(null);
    }
  }, [chantierId]);

  useEffect(() => { charger(); }, [charger, refreshKey]);

  // Archive en préparation : on suit l'avancement
  useEffect(() => {
    if (etat?.archive?.status !== 'processing') return undefined;
    const t = setTimeout(charger, 3000);
    return () => clearTimeout(t);
  }, [etat, charger]);

  if (!etat) return null;
  const cl = etat.cloture;

  const telecharger = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/chantiers/${chantierId}/export-archive/${etat.archive.job_id}/download`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = etat.archive.zip_name || 'archive.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  const rouvrir = async () => {
    if (!window.confirm('Rouvrir ce chantier ? Le client et les entreprises retrouveront leurs accès.')) return;
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/cloture/rouvrir`, {}, { withCredentials: true });
      toast.success('Chantier rouvert');
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (cl) {
    return (
      <div className="bg-[#F5F5F3] border border-[#E0E0DB] p-4 space-y-2" data-testid="cloture-etat">
        <div className="flex items-center gap-2">
          <LockKey size={18} className="text-[#4A4A4A]" />
          <h3 className="font-medium text-[#2C2C2C]">Chantier clôturé le {dateFr(cl.le)}{cl.par_nom ? ` par ${cl.par_nom}` : ''}</h3>
        </div>
        <p className="text-sm text-[#4A4A4A]">
          {etat.acces_externes === 'lecture_seule'
            ? <>Le client et les entreprises consultent le dossier en lecture seule jusqu'au {dateFr(cl.acces_externes_jusqu_au)} (fin de la garantie), puis leur accès sera fermé.</>
            : <>L'accès du client et des entreprises est fermé.</>}
        </p>
        {isMoe && etat.archive && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {etat.archive.status === 'completed' && (
              <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={telecharger} data-testid="cloture-archive">
                <DownloadSimple size={14} /> Télécharger l'archive du chantier
              </Button>
            )}
            {etat.archive.status === 'processing' && <span className="text-[#6B6B6B]">Archive en préparation… {etat.archive.progress || 0} %</span>}
            {etat.archive.status === 'failed' && <span className="text-[#8B3A3A]">L'archive n'a pas pu être préparée : réessayez depuis « Supprimer le chantier › Générer l'archive ».</span>}
          </div>
        )}
        {etat.peut_rouvrir && (
          <button type="button" className="text-xs underline text-[#6B6B6B]" onClick={rouvrir}>Rouvrir le chantier</button>
        )}
      </div>
    );
  }

  if (!etat.peut_cloturer) return null;
  return (
    <div className="bg-white border border-[#E0E0DB] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="cloture-bloc">
      <div>
        <h3 className="font-medium text-[#2C2C2C] flex items-center gap-2"><Archive size={18} /> Clôturer le chantier</h3>
        <p className="text-sm text-[#6B6B6B]">Archive complète conservée, accès du client et des entreprises en lecture seule jusqu'à la fin de la garantie. Rien n'est supprimé.</p>
      </div>
      <Button variant="outline" className="rounded-none" onClick={() => setOuvrir(true)} data-testid="cloture-ouvrir">Clôturer le chantier</Button>
      {ouvrir && <ClotureDialog chantierId={chantierId} onClose={() => setOuvrir(false)} onDone={() => { setOuvrir(false); charger(); }} />}
    </div>
  );
}

function ClotureDialog({ chantierId, onClose, onDone }) {
  const [verif, setVerif] = useState(null);
  const [confirme, setConfirme] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/chantiers/${chantierId}/cloture/verifications`, { withCredentials: true })
      .then(({ data }) => setVerif(data)).catch(() => setVerif({ checks: [], tout_ok: false }));
  }, [chantierId]);

  const ouverts = (verif?.checks || []).filter((c) => !c.ok && c.cle !== 'fin_gpa');

  const cloturer = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/cloture`, { confirmer: confirme }, { withCredentials: true });
      toast.success("Chantier clôturé : l'archive est en préparation");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clôture impossible');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="cloture-dialog">
        <DialogHeader><DialogTitle>Clôturer le chantier</DialogTitle></DialogHeader>
        {!verif ? <p className="text-sm text-[#6B6B6B]">Vérifications…</p> : (
          <div className="space-y-3">
            <ul className="space-y-1.5">
              {verif.checks.map((c) => (
                <li key={c.cle} className="flex gap-2 text-sm" data-testid={`cloture-check-${c.cle}`}>
                  {c.ok ? <CheckCircle size={18} className="text-[#2D6A4F] shrink-0" weight="fill" /> : <Warning size={18} className="text-[#B8860B] shrink-0" weight="fill" />}
                  <span><strong className="font-medium">{c.libelle}</strong><br /><span className="text-[#6B6B6B]">{c.detail}</span></span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#6B6B6B]">
              La clôture prépare l'archive complète du chantier (PV, réserves avec photos et preuves, signatures, comptes rendus,
              documents, plans annotés, situations) et la conserve. Le client et les entreprises passent en lecture seule
              jusqu'à la fin de la garantie, puis leur accès est fermé. Le chantier sort des colonnes actives du tableau.
            </p>
            {ouverts.length > 0 && (
              <label className="flex items-start gap-2 text-sm text-[#8B3A3A]">
                <input type="checkbox" className="mt-1" checked={confirme} onChange={(e) => setConfirme(e.target.checked)} data-testid="cloture-confirmer" />
                Je clôture malgré {ouverts.length === 1 ? 'le point ouvert' : `les ${ouverts.length} points ouverts`} ci-dessus.
              </label>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-none" onClick={onClose}>Annuler</Button>
              <Button className="rounded-none bg-[#2C2C2C] hover:bg-[#4A4A4A]" disabled={envoi || (ouverts.length > 0 && !confirme)} onClick={cloturer} data-testid="cloture-valider">
                Clôturer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
